import Vue from "vue";
import { v4 as uuid } from "uuid";
import i18n from "@/locales";
import DismissAction from "@/components/global-notification/dismiss-action.vue";

const VERSION_URL = "/version.json";
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const INITIAL_DELAY_MS = 30 * 1000;

// __APP_VERSION__ is injected by webpack DefinePlugin in vue.config.js
// (always defined in both dev and prod builds). Fallback to null only
// guards against the constant being stripped in some other build context.
const CURRENT_VERSION =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : null;

// Track whether the prompt is currently on screen so we don't stack
// duplicates. If the user dismisses without reloading, onClose flips
// this back to false and the next poll re-shows the prompt — so users
// never end up silently stuck on an old build.
let toastShowing = false;

async function checkForUpdate() {
  if (toastShowing || !CURRENT_VERSION) return;
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: "no-store",
      credentials: "omit",
    });
    if (!res.ok) return;
    const { version } = await res.json();
    if (version && version !== CURRENT_VERSION) {
      toastShowing = true;
      const toastId = uuid();
      Vue.$toast.info(
        {
          component: DismissAction,
          props: {
            toast_id: toastId,
            message: i18n.t("newVersionAvailable"),
            dismiss_button_text: i18n.t("dismiss"),
            action_button_text: i18n.t("Reload"),
            on_action_button_click: () => window.location.reload(),
          },
        },
        {
          id: toastId,
          icon: false,
          timeout: false,
          closeOnClick: false,
          onClose: () => {
            toastShowing = false;
          },
        },
      );
    }
  } catch {
    // Network blip or offline — retry on the next interval.
  }
}

export function startVersionCheck() {
  // Skip in dev: vue.config.js stamps a fresh Date.now() into both
  // __APP_VERSION__ and public/version.json on every dev-server restart,
  // so any restart-without-hard-refresh would spam the reload toast.
  if (process.env.NODE_ENV !== "production") return;
  if (!CURRENT_VERSION) return;

  setTimeout(checkForUpdate, INITIAL_DELAY_MS);

  setInterval(() => {
    if (document.visibilityState === "visible") {
      checkForUpdate();
    }
  }, POLL_INTERVAL_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      checkForUpdate();
    }
  });
}
